/**
 * VÉRAULT Member Suite — RouteMap (Wave 26.24 · Phase 3b).
 * ─────────────────────────────────────────────────────────
 * Editorial replacement for the legacy advisor DayMap inside the
 * client itinerary. Two non-negotiable departures from the legacy:
 *
 *   1 · **Cross-highlight** — the map and the timeline share a
 *       `focusedKey`. Hovering/tapping a pin highlights the matching
 *       activity card; hovering/tapping a card highlights the pin.
 *
 *   2 · **Leg context** — between consecutive pins we surface a tiny
 *       Haversine distance + a walking-time estimate
 *       ("0.8 km · ~10 min walk"). Not driving directions
 *       (that'd need Google) — just luxurious situational context.
 *
 * Discipline:
 *   · Pure SVG. No tile layer. No external service. No giant embed.
 *   · Silent when fewer than 1 geocoded stop exists.
 *   · Calm 21:8 aspect — wider, less tall than the legacy 16:9, so
 *     the map sits beside the timeline without dominating.
 *   · Numbered pin scheme matches the ActivityCard's 01..NN numbering
 *     so a member can scan top-down without translating identifiers.
 */
import React from "react";
import { MapPin } from "lucide-react";

const VALID_BLOCKS = ["morning", "afternoon", "evening", "late_night"];
const VALID_SLOTS = ["meal_slot", "primary_activity", "secondary_activity"];

// ─── Utilities ──────────────────────────────────────────────────────
function projectMercator(lat, lng) {
  const x = (lng + 180) / 360;
  const sinL = Math.sin((lat * Math.PI) / 180);
  const y = 0.5 - Math.log((1 + sinL) / (1 - sinL)) / (4 * Math.PI);
  return { x, y };
}

function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

function formatLeg(km) {
  // Wave 11g · Phase B+ — USA-first units. Walking speed ~3.1 mph
  // (5 km/h). Distances surface in feet / miles. Under ~165 ft collapse
  // to "right next door". Anything > 3 mi is likely a transfer.
  const mi = km * 0.621371;
  const walkMin = Math.round((km / 5) * 60); // walk time unchanged
  if (mi < 0.03) return "right next door";              // < ~160 ft
  if (mi < 0.19) {
    const ft = Math.round(km * 3280.84);                // < 0.19 mi → feet
    return `${ft} ft · ~${walkMin} min walk`;
  }
  if (mi < 3) return `${mi.toFixed(1)} mi · ~${walkMin} min walk`;
  // > 3 mi — almost always a vehicle leg, not a walk
  return `${mi.toFixed(1)} mi away`;
}

/**
 * Walk a day in chronological order, attach a stable focusKey per item
 * so the timeline can cross-link.
 */
export function collectGeocodedPoints(day) {
  if (!day) return [];
  const pts = [];
  const visit = (it, blockKey, slot, idxInBlock) => {
    if (!it) return;
    const lat = typeof it.latitude === "number" ? it.latitude : it.lat;
    const lng = typeof it.longitude === "number" ? it.longitude : it.lng;
    if (typeof lat !== "number" || typeof lng !== "number") return;
    pts.push({
      // focusKey MUST match what the timeline emits; (blockKey, indexInBlock)
      // is the contract.
      focusKey: `${blockKey}-${idxInBlock}`,
      id: it.id || `${blockKey}-${slot}-${pts.length}`,
      name: it.name || it.title || it.activity || "Stop",
      lat,
      lng,
      block: blockKey,
    });
  };

  for (const bk of VALID_BLOCKS) {
    const block = day[bk];
    if (!block) continue;
    // Build flat per-block index list mirroring ActivityTimeline.
    let perBlockIdx = 0;
    if (Array.isArray(block)) {
      block.forEach((it) => { visit(it, bk, "item", perBlockIdx); perBlockIdx += 1; });
    } else if (Array.isArray(block.items)) {
      block.items.forEach((it) => { visit(it, bk, "item", perBlockIdx); perBlockIdx += 1; });
    } else if (typeof block === "object") {
      for (const slot of VALID_SLOTS) {
        if (block[slot]) { visit(block[slot], bk, slot, perBlockIdx); perBlockIdx += 1; }
      }
      for (const it of block.extra_items || []) {
        visit(it, bk, "extra", perBlockIdx); perBlockIdx += 1;
      }
    }
  }
  return pts;
}

// ─── The component ──────────────────────────────────────────────────
export function RouteMap({
  day,
  focusedKey,
  onFocusChange,
  className = "",
  testid,
}) {
  const points = React.useMemo(() => collectGeocodedPoints(day), [day]);
  const projected = React.useMemo(
    () => points.map((p) => ({ ...p, m: projectMercator(p.lat, p.lng) })),
    [points],
  );
  if (points.length === 0) return null;

  // Projection canvas — 21:8 aspect (wider, less tall than legacy 16:9).
  const W = 1000;
  const H = 380;
  const PAD = 50;

  // Compute padded bounding box.
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  // Inflate a single-point box ~250m so it still feels centered.
  if (points.length === 1 || (minLat === maxLat && minLng === maxLng)) {
    const inflate = 0.003;
    minLat -= inflate; maxLat += inflate; minLng -= inflate; maxLng += inflate;
  }
  const c1 = projectMercator(maxLat, minLng);
  const c2 = projectMercator(minLat, maxLng);
  let bx0 = Math.min(c1.x, c2.x);
  let bx1 = Math.max(c1.x, c2.x);
  let by0 = Math.min(c1.y, c2.y);
  let by1 = Math.max(c1.y, c2.y);
  // Soft pad so pins never kiss the frame.
  const dx = (bx1 - bx0) || 1e-6;
  const dy = (by1 - by0) || 1e-6;
  bx0 -= dx * 0.08; bx1 += dx * 0.08;
  by0 -= dy * 0.12; by1 += dy * 0.12;
  // Lock aspect ratio.
  const targetAspect = (W - PAD * 2) / (H - PAD * 2);
  const bboxAspect = (bx1 - bx0) / (by1 - by0);
  if (bboxAspect > targetAspect) {
    const nDy = (bx1 - bx0) / targetAspect;
    const cy = (by0 + by1) / 2;
    by0 = cy - nDy / 2; by1 = cy + nDy / 2;
  } else {
    const nDx = (by1 - by0) * targetAspect;
    const cx = (bx0 + bx1) / 2;
    bx0 = cx - nDx / 2; bx1 = cx + nDx / 2;
  }

  const toX = (m) => PAD + ((m.x - bx0) / (bx1 - bx0)) * (W - PAD * 2);
  const toY = (m) => PAD + ((m.y - by0) / (by1 - by0)) * (H - PAD * 2);

  const polyline = projected.map((p) => `${toX(p.m)},${toY(p.m)}`).join(" ");

  // Mid-point labels for legs (skip when >2 stops to avoid clutter — show
  // only the first leg's label so the map breathes; the legend below
  // lists all legs).
  const legs = [];
  for (let i = 0; i < projected.length - 1; i += 1) {
    const km = haversineKm(projected[i], projected[i + 1]);
    legs.push({ from: projected[i], to: projected[i + 1], km, label: formatLeg(km) });
  }

  return (
    <div
      className={`rounded-md overflow-hidden bg-[#FAF7F2] border border-[rgba(0,0,0,0.04)] ${className}`}
      data-testid={testid || `route-map-day-${day?.day_number}`}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full h-auto"
        role="img"
        aria-label={`Route map for day ${day?.day_number}`}
      >
        <defs>
          <pattern id={`paper-${day?.day_number}`} width="6" height="6" patternUnits="userSpaceOnUse">
            <rect width="6" height="6" fill="#FAF7F2" />
            <circle cx="1" cy="1" r="0.5" fill="rgba(0,0,0,0.04)" />
          </pattern>
        </defs>
        <rect width={W} height={H} fill={`url(#paper-${day?.day_number})`} />

        {/* Connecting polyline — calm dashed */}
        {projected.length > 1 && (
          <polyline
            points={polyline}
            fill="none"
            stroke="rgba(146,115,75,0.42)"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeDasharray="4 5"
          />
        )}

        {/* Pins (rendered after polyline so they sit on top) */}
        {projected.map((p, i) => {
          const x = toX(p.m);
          const y = toY(p.m);
          const isFocused = focusedKey && focusedKey === p.focusKey;
          const setFocus = () => onFocusChange && onFocusChange(p.focusKey);
          const clearFocus = () => onFocusChange && onFocusChange(null);
          return (
            <g
              key={`${p.id}-${i}`}
              data-testid={`route-map-pin-${day?.day_number}-${i + 1}`}
              data-focus-key={p.focusKey}
              data-focused={isFocused ? "true" : "false"}
              // Triple-bind for robust SVG hover detection across
              // browsers + Playwright synthetic events. Pointer events
              // are the primary signal; mouse events backstop them.
              onPointerEnter={setFocus}
              onPointerLeave={clearFocus}
              onMouseOver={setFocus}
              onMouseOut={clearFocus}
              onFocus={setFocus}
              onBlur={clearFocus}
              onClick={() => onFocusChange && onFocusChange(isFocused ? null : p.focusKey)}
              tabIndex={0}
              style={{ cursor: "pointer", pointerEvents: "all" }}
            >
              {/* Larger transparent hit-target so the entire halo zone
                  fires the hover, not just the 11px pin circle. */}
              <circle cx={x} cy={y} r="22" fill="transparent" />
              {/* Soft halo when focused */}
              {isFocused && (
                <circle cx={x} cy={y} r="20" fill="rgba(212,175,55,0.22)" />
              )}
              <circle cx={x} cy={y + 2} r="11" fill="rgba(0,0,0,0.08)" />
              <circle
                cx={x}
                cy={y}
                r="11"
                fill={isFocused ? "#D4AF37" : "#FFFFFF"}
                stroke={isFocused ? "#92734B" : "#92734B"}
                strokeWidth={isFocused ? "1.8" : "1.4"}
              />
              <text
                x={x}
                y={y + 4}
                textAnchor="middle"
                fontFamily="ui-monospace, monospace"
                fontSize="11"
                fontWeight="600"
                fill={isFocused ? "#1A1A1A" : "#92734B"}
                style={{ pointerEvents: "none" }}
              >
                {i + 1}
              </text>
            </g>
          );
        })}

        {/* First-leg distance label — quiet, only when 2+ pins. Skip when
            it would be cluttered. */}
        {legs.length === 1 && (
          <text
            x={(toX(legs[0].from.m) + toX(legs[0].to.m)) / 2}
            y={(toY(legs[0].from.m) + toY(legs[0].to.m)) / 2 - 10}
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="10"
            letterSpacing="1.5"
            fill="rgba(0,0,0,0.45)"
          >
            {legs[0].label.toUpperCase()}
          </text>
        )}
      </svg>

      {/* Leg legend below — chronological. Highlights the same focus state. */}
      <ul
        className="px-3.5 py-3 text-[12.5px] text-[hsl(var(--foreground))] divide-y divide-[rgba(0,0,0,0.04)]"
        data-testid={`route-map-legend-${day?.day_number}`}
      >
        {projected.map((p, i) => {
          const isFocused = focusedKey && focusedKey === p.focusKey;
          const next = projected[i + 1];
          const leg = next ? haversineKm(p, next) : null;
          return (
            <li key={`${p.id}-leg-${i}`} className="py-1.5">
              <button
                type="button"
                className={`w-full flex items-center gap-2.5 group text-left transition-colors ${isFocused ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-secondary))]"}`}
                onMouseEnter={() => onFocusChange && onFocusChange(p.focusKey)}
                onMouseLeave={() => onFocusChange && onFocusChange(null)}
                onClick={() => onFocusChange && onFocusChange(isFocused ? null : p.focusKey)}
                data-testid={`route-map-leg-${day?.day_number}-${i + 1}`}
              >
                <span
                  className={`inline-flex items-center justify-center size-5 rounded-full font-mono text-[10px] font-semibold flex-shrink-0 transition-colors ${
                    isFocused
                      ? "bg-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))] text-[hsl(var(--foreground))]"
                      : "border border-[hsl(var(--accent-gold))] text-[hsl(var(--accent-gold))]"
                  }`}
                >
                  {i + 1}
                </span>
                <MapPin className="size-3 text-[hsl(var(--foreground-muted))] flex-shrink-0" strokeWidth={1.5} />
                <span className="font-serif text-[13px] truncate flex-1">{p.name}</span>
              </button>
              {leg != null && (
                <p className="pl-[34px] mt-0.5 font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                  ↓ {formatLeg(leg)}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default RouteMap;
